import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { Link, generatePath } from 'react-router-dom';
import {
  apiService,
  useBillingUsageControllerCallsQuery,
  type BillingUsageCallDto,
} from '@shared/services/apiService';
import { AuthenticatedPath } from '@shared/constants/routes.constants';
import { formatCreditUnits } from '../model/formatBillingUnits';
import { billingCallsCsv } from '../model/billingCsv';
type Args = { companyId: string; from: string; to: string; campaignId?: string; agentId?: string };
export const BillingUsageCalls = ({ args }: { args: Args }) => {
  const query = useBillingUsageControllerCallsQuery(args, { refetchOnMountOrArgChange: true });
  const [load] = apiService.useLazyBillingUsageControllerCallsQuery();
  const [older, setOlder] = useState<BillingUsageCallDto[]>([]),
    [cursor, setCursor] = useState<string | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(false);
  const epoch = useRef(0);
  useEffect(() => {
    const currentEpoch = epoch.current + 1;
    epoch.current = currentEpoch;
    setOlder([]);
    setCursor(null);
    setError(false);
    return () => {
      epoch.current = currentEpoch + 1;
    };
  }, [args.companyId, args.from, args.to, args.campaignId, args.agentId]);
  const data = query.currentData;
  const rows = [...(data?.calls ?? []), ...older].filter(
    (r, i, a) => a.findIndex((x) => x.callId === r.callId) === i,
  );
  const next = cursor ?? data?.nextCursor;
  const more = async () => {
    const version = epoch.current;
    setBusy(true);
    setError(false);
    try {
      const p = await load({ ...args, after: next! }, false).unwrap();
      if (
        data &&
        (p.creditScale !== data.creditScale ||
          p.chargingPolicyVersion !== data.chargingPolicyVersion)
      )
        throw new Error('credit_policy_changed');
      if (version === epoch.current) {
        setOlder((a) => [...a, ...p.calls]);
        setCursor(p.nextCursor ?? '');
      }
    } catch {
      if (version === epoch.current) setError(true);
    } finally {
      setBusy(false);
    }
  };
  const exportAll = async () => {
    const version = epoch.current;
    setBusy(true);
    setError(false);
    try {
      const first = await load(args, false).unwrap();
      let page = first;
      const all = [...first.calls];
      const seen = new Set<string>();
      while (page.nextCursor) {
        if (seen.has(page.nextCursor)) throw new Error('cursor_loop');
        seen.add(page.nextCursor);
        page = await load({ ...args, after: page.nextCursor }, false).unwrap();
        if (version !== epoch.current) return;
        if (
          page.creditScale !== first.creditScale ||
          page.chargingPolicyVersion !== first.chargingPolicyVersion
        )
          throw new Error('credit_policy_changed');
        all.push(...page.calls);
      }
      if (version !== epoch.current) return;
      const url = URL.createObjectURL(
        new Blob([billingCallsCsv(all, first.creditScale)], { type: 'text/csv;charset=utf-8' }),
      );
      const a = document.createElement('a');
      a.href = url;
      a.download = `call-usage-${args.from.slice(0, 10)}-${args.to.slice(0, 10)}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      if (version === epoch.current) setError(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Stack spacing={1} aria-label="Billed calls">
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="subtitle2">Billed calls</Typography>
        <Button size="small" disabled={busy || query.isFetching} onClick={() => void exportAll()}>
          Export all matching calls (CSV)
        </Button>
      </Stack>
      {(error || query.isError) && (
        <Alert severity="warning">Calls could not be loaded. Try again.</Alert>
      )}
      {query.isFetching && <Typography variant="body2">Loading billed calls…</Typography>}
      {rows.map((r) => (
        <Box
          key={r.callId}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            py: 1,
            flexWrap: 'wrap',
          }}>
          <Box>
            {r.campaignId ? (
              <Button
                size="small"
                component={Link}
                to={generatePath(AuthenticatedPath.CALL_DETAIL, {
                  campaignId: r.campaignId,
                  callId: r.callId,
                })}>
                Open call · {r.date}
              </Button>
            ) : (
              <Typography variant="body2">{r.date} · Campaign unavailable</Typography>
            )}
            <Typography variant="caption" component="p" color="text.secondary">
              {r.campaignName} · {r.agentName} · {r.callId}
            </Typography>
          </Box>
          <Typography variant="body2">
            {formatCreditUnits(r.totalUnits, data?.creditScale ?? 0)} credits
          </Typography>
        </Box>
      ))}
      {data && !rows.length && (
        <Typography variant="body2">No billed calls in this selection.</Typography>
      )}
      {!!next && (
        <Button disabled={busy} onClick={() => void more()}>
          Load more billed calls
        </Button>
      )}
    </Stack>
  );
};
